import { isStoreMemberInterviewPath, isStoreTokenInterviewPath } from '@/constants/interviewRoutes'

const getDefaultState = () => {
  const jdUrl = localStorage.getItem('jd_url')
  const jdName = localStorage.getItem('jd_name')
  const cvUrl = localStorage.getItem('cv_url')
  const cvName = localStorage.getItem('cv_name')

  return {
    interviewDuration: 10, // 与 dashboard 可选 10/20/30 一致，并作为题目数量传给 questions/generate
    actualInterviewDuration: 0, // 新增：实际面试时长（分钟）
    interviewLanguage: '', // 新增：存储面试语言
    usingBeautifys: [],
    usingFilter: null,
    usingBackground: null,
    interviewStarted: false,
    interviewId: '',
    jd: jdUrl ? { url: jdUrl, name: jdName } : null,
    cv: cvUrl ? { url: cvUrl, name: cvName } : null,
    prompt: null,
    formattedDialogues: '',
    rawDialogues: [], // 面试中对话；上传 transcript 与 formatDialogues 使用
    promptLoading: false,
    promptError: null,
    linkOpenData: null, // 面试链接打开接口返回（nextAction=START_INTERVIEW 时的 data）
    /** 正常 Cathy 流程：generate_questions_c 返回后写入，供 interviewUnified 与欢迎语 */
    toCInterviewMeta: null, // { questionOutline: string[], candidateName: string } | null
    /** 结束面试时写入，generateInterview 用 time_factor / timeFactor（已问÷总题，两位小数字符串如 "0.33"） */
    interviewProgressSnapshot: null,
    currentInterviewJob: null,
    leaveScreenCount: 0, // 行为检测：不在画面中的次数（结束面试写入 transcript；每场开始会清零）
    multiplePeopleCount: 0, // 行为检测：多人入镜次数（同上）
    cloudRecordTaskId: '', // 云端录制 TaskId
    cloudRecordFilePattern: '', // 云端录制文件命名 Pattern（随机生成，传给后端做关联）
    cloudRecordVideoUrl: '', // 云端录制视频地址，传给 generateInterview 的 videoUrl
    interviewStartTimestamp: 0, // 开始面试后的计时起点（毫秒时间戳），从 0 开始计时的基准
    aiSpeechSegments: [], // 每次 AI 说话的起止时间 [{ start, end }, ...]，单位为秒（相对面试开始）
  }
}



const state = getDefaultState()

const mutations = {
  SET_FORMATTED_DIALOGUES: (state, formattedText) => {
    state.formattedDialogues = formattedText
  },
  RESET_STATE: (state) => {
    Object.assign(state, getDefaultState())
  },
  SET_INTERVIEW_DURATION: (state, duration) => {
    state.interviewDuration = duration
  },
  SET_ACTUAL_INTERVIEW_DURATION: (state, duration) => {
    state.actualInterviewDuration = duration
  },
  SET_INTERVIEW_LANGUAGE: (state, language) => { // 新增：设置面试语言
    state.interviewLanguage = language
  },
  SET_USING_BEAUTIFYS: (state, beautifys) => {
    state.usingBeautifys = beautifys
  },
  SET_USING_FILTER: (state, filter) => {
    state.usingFilter = filter
  },
  SET_USING_BACKGROUND: (state, background) => {
    state.usingBackground = background
  },
  SET_PROMPT(state, prompt) {
    state.prompt = prompt
  },
  SET_INTERVIEW_STARTED: (state, started) => {
    state.interviewStarted = started
    if (started) {
      state.rawDialogues = []
    }
  },
  SET_INTERVIEW_ID: (state, id) => {
    state.interviewId = id
  },
  SET_BEHAVIOR_DETECTION_COUNTS: (state, { leaveScreenCount = 0, multiplePeopleCount = 0 } = {}) => {
    state.leaveScreenCount = leaveScreenCount
    state.multiplePeopleCount = multiplePeopleCount
  },
  SET_JD: (state, jd) => {
    state.jd = jd
    // 保存URL和文件名到localStorage
    if (jd && jd.url) {
      localStorage.setItem('jd_url', jd.url)
      localStorage.setItem('jd_name', jd.name || 'job_description.txt')
    } else {
      localStorage.removeItem('jd_url')
      localStorage.removeItem('jd_name')
    }
  },
  SET_CV: (state, cv) => {
    state.cv = cv
    // 保存URL和文件名到localStorage
    if (cv && cv.url) {
      localStorage.setItem('cv_url', cv.url)
      localStorage.setItem('cv_name', cv.name || 'resume.pdf')
    } else {
      localStorage.removeItem('cv_url')
      localStorage.removeItem('cv_name')
    }
  },
  SET_DIALOGUES: (state, dialogue) => {
    state.rawDialogues.push(dialogue)
  },
  CLEAR_DIALOGUES: (state) => {
    state.rawDialogues = []
    state.formattedDialogues = ''
  },
  SET_PROMPT_LOADING: (state, loading) => {
    state.promptLoading = loading
  },
  SET_PROMPT_ERROR: (state, error) => {
    state.promptError = error || null
  },
  SET_LINK_OPEN_DATA: (state, data) => {
    state.linkOpenData = data || null
  },
  SET_TO_C_INTERVIEW_META: (state, meta) => {
    state.toCInterviewMeta = meta && typeof meta === 'object'
      ? {
          questionOutline: Array.isArray(meta.questionOutline) ? meta.questionOutline : [],
          candidateName: typeof meta.candidateName === 'string' ? meta.candidateName : '候选人'
        }
      : null
  },
  SET_INTERVIEW_PROGRESS_SNAPSHOT(state, snapshot) {
    if (snapshot && typeof snapshot === 'object') {
      const current = typeof snapshot.current === 'number' && !Number.isNaN(snapshot.current) ? snapshot.current : 0
      const total = typeof snapshot.total === 'number' && !Number.isNaN(snapshot.total) ? snapshot.total : 0
      state.interviewProgressSnapshot = { current, total }
    } else {
      state.interviewProgressSnapshot = null
    }
  },
  SET_CURRENT_INTERVIEW_JOB(state, job) {
    state.currentInterviewJob = job ? { ...job } : null
  },
  CLEAR_CURRENT_INTERVIEW_JOB(state) {
    state.currentInterviewJob = null
  },
  SET_CLOUD_RECORD_INFO(state, { taskId = '', filePattern = '', videoUrl = '' } = {}) {
    state.cloudRecordTaskId = taskId ? String(taskId) : ''
    state.cloudRecordFilePattern = filePattern ? String(filePattern) : ''
    state.cloudRecordVideoUrl = videoUrl ? String(videoUrl) : ''
  },
  SET_INTERVIEW_START_TIMESTAMP(state, timestamp) {
    state.interviewStartTimestamp = timestamp || 0
  },
  SET_AI_SPEECH_START_RESET(state) {
    state.aiSpeechSegments = []
  },
  RECORD_AI_SPEECH_START(state, elapsedSeconds) {
    if (typeof elapsedSeconds !== 'number' || Number.isNaN(elapsedSeconds)) return
    const segs = state.aiSpeechSegments
    if (segs.length > 0 && segs[segs.length - 1].end === 0) return
    state.aiSpeechSegments.push({ start: elapsedSeconds, end: 0 })
  },
  RECORD_AI_SPEECH_END(state, elapsedSeconds) {
    const segs = state.aiSpeechSegments
    if (segs.length > 0 && typeof elapsedSeconds === 'number' && !Number.isNaN(elapsedSeconds)) {
      const last = segs[segs.length - 1]
      if (last.end === 0) last.end = elapsedSeconds
    }
  }
}

const actions = {
  // 与 token 流程一致：后端未返回 interview_id 时，前端兜底生成 UUID
  _generateInterviewId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  },
  /**
   * 每次开面前清空「上一场面试」残留：除简历(cv)、JD 文件(jd) 外全部重置。
   * 面试时长 / 语言 / 热门岗位由调用方在 reset 后按需写回（见 dashboard goToStep2）。
   */
  /**
   * 路由进入 /store/interview_ing | /store/interview_ing_by_b 时集中清理，避免各入口漏 commit。
   *
   * 入口梳理（是否都会触发本方法：是，经 permission 全局 beforeEach）：
   * | 入口 | 路由 | 说明 |
   * |------|------|------|
   * | 仪表盘微笑步→启动 | interview_ing / interview_ing_by_b | 会员：startInterview 后 push；token：SET_LINK_OPEN_DATA 后 push |
   * | 投递弹窗「立即面试」 | interview_ing | 仅跳转；未 start 时 created 会踢回 profile |
   * | 直接打开/收藏 URL | 同上 | 同上 |
   * | 旧路径重定向 | /interview_ing → /store/interview_ing | 第二次导航命中本逻辑 |
   *
   * 策略：
   * - 会员页：去掉 token 流程残留（linkOpenData 等）；未 start 时等价 resetInterviewState（与步进 2 一致，清上一场残留）。
   * - Token 页：去掉 Cathy 会员残留（prompt/toCMeta）；绝不清 linkOpenData；未 start 时仅清对话等轻量字段（避免误伤刷新场景下的进行中会话，不在此清 dialogues）。
   */
  prepareInterviewRouteEntry({ state, commit, dispatch }, { toPath } = {}) {
    const tp = toPath || ''
    const isMember = isStoreMemberInterviewPath(tp)
    const isToken = isStoreTokenInterviewPath(tp)
    if (!isMember && !isToken) {
      return Promise.resolve()
    }

    if (isMember) {
      commit('SET_LINK_OPEN_DATA', null)
      commit('SET_INTERVIEW_PROGRESS_SNAPSHOT', null)
      if (!state.interviewStarted) {
        return dispatch('resetInterviewState')
      }
      commit('SET_PROMPT_ERROR', null)
      commit('SET_PROMPT_LOADING', false)
      return Promise.resolve()
    }

    // token 页：保留 linkOpenData / 面试进行中对话
    commit('SET_PROMPT', null)
    commit('SET_TO_C_INTERVIEW_META', null)
    commit('SET_PROMPT_ERROR', null)
    commit('SET_PROMPT_LOADING', false)
    if (!state.interviewStarted) {
      commit('CLEAR_DIALOGUES')
      commit('SET_INTERVIEW_ID', '')
      commit('SET_ACTUAL_INTERVIEW_DURATION', 0)
      commit('SET_CLOUD_RECORD_INFO', { taskId: '', filePattern: '', videoUrl: '' })
      commit('SET_INTERVIEW_START_TIMESTAMP', 0)
      commit('SET_AI_SPEECH_START_RESET')
      commit('SET_BEHAVIOR_DETECTION_COUNTS', { leaveScreenCount: 0, multiplePeopleCount: 0 })
    }
    return Promise.resolve()
  },

  resetInterviewState({ state, commit }) {
    const jd = state.jd && typeof state.jd === 'object' ? { ...state.jd } : null
    const cv = state.cv && typeof state.cv === 'object' ? { ...state.cv } : null

    commit('CLEAR_DIALOGUES')
    commit('SET_INTERVIEW_STARTED', false)
    commit('SET_PROMPT', null)
    commit('SET_PROMPT_ERROR', null)
    commit('SET_PROMPT_LOADING', false)
    commit('SET_LINK_OPEN_DATA', null)
    commit('SET_TO_C_INTERVIEW_META', null)
    commit('SET_INTERVIEW_ID', '')
    commit('SET_ACTUAL_INTERVIEW_DURATION', 0)
    commit('SET_CLOUD_RECORD_INFO', { taskId: '', filePattern: '', videoUrl: '' })
    commit('SET_INTERVIEW_START_TIMESTAMP', 0)
    commit('SET_AI_SPEECH_START_RESET')
    commit('SET_INTERVIEW_PROGRESS_SNAPSHOT', null)
    commit('SET_USING_BEAUTIFYS', [])
    commit('SET_USING_FILTER', null)
    commit('SET_USING_BACKGROUND', null)
    commit('SET_BEHAVIOR_DETECTION_COUNTS', { leaveScreenCount: 0, multiplePeopleCount: 0 })
    commit('SET_INTERVIEW_DURATION', 10)
    commit('SET_INTERVIEW_LANGUAGE', '')
    commit('CLEAR_CURRENT_INTERVIEW_JOB')

    commit('SET_JD', jd)
    commit('SET_CV', cv)
  },
  setInterviewStartTimestamp({ commit }, timestamp) {
    commit('SET_INTERVIEW_START_TIMESTAMP', timestamp)
    commit('SET_AI_SPEECH_START_RESET')
  },
  recordAiSpeechStart({ commit, state }, elapsedSeconds) {
    if (state.interviewStartTimestamp) {
      const elapsed = typeof elapsedSeconds === 'number' ? elapsedSeconds : (Date.now() - state.interviewStartTimestamp) / 1000
      commit('RECORD_AI_SPEECH_START', elapsed)
    }
  },
  recordAiSpeechEnd({ commit, state }, elapsedSeconds) {
    if (state.interviewStartTimestamp) {
      const elapsed = typeof elapsedSeconds === 'number' ? elapsedSeconds : (Date.now() - state.interviewStartTimestamp) / 1000
      commit('RECORD_AI_SPEECH_END', elapsed)
    }
  },
  formatDialogues({ commit, state }) {
    return new Promise((resolve) => {
      let aiMessageIndex = 0
      const segs = state.aiSpeechSegments || []
      const buffer = { texts: [], firstStart: null }
      const isReminder = (text) => {
        const t = (text || '').trim()
        return t.includes('小提醒') || /^Reminder\s*[：:]?/i.test(t)
      }
      const flushInterviewer = (acc) => {
        if (buffer.texts.length === 0) return acc
        const merged = buffer.texts.join('\n\n')
        const start = buffer.firstStart
        const ts = typeof start === 'number' && !Number.isNaN(start) ? ` [timestamp:${start.toFixed(1)}]` : ''
        buffer.texts = []
        buffer.firstStart = null
        return acc + `[面试官] ${merged}${ts}\n`
      }
      const getInterviewerStart = (item) => {
        if (item && typeof item === 'object') {
          const v = item.timestampStart ?? item.timestamp ?? item.tsStart
          if (typeof v === 'number' && !Number.isNaN(v)) return v
        }
        const start = segs[aiMessageIndex]?.start
        aiMessageIndex += 1
        return start
      }

      let out = ''
      for (let i = 0; i < state.rawDialogues.length; i++) {
        const item = state.rawDialogues[i]
        if (typeof item !== 'object' || item === null) {
          out = flushInterviewer(out)
          const text = typeof item === 'string' ? item : ''
          if (text) out += `[候选人] ${text}\n`
          continue
        }
        const normalizedUserId = (item.userId || '').replace(/[＿_]/g, '')
        const isInterviewer = normalizedUserId === 'RobotMan'
        const text = `${item.text ?? ''}`
        if (isInterviewer) {
          const start = getInterviewerStart(item)
          if (isReminder(text)) {
            out = flushInterviewer(out)
            const ts = typeof start === 'number' && !Number.isNaN(start) ? ` [timestamp:${start.toFixed(1)}]` : ''
            out += `[系统提示] ${text}${ts}\n`
          } else {
            buffer.texts.push(text)
            if (buffer.firstStart == null && typeof start === 'number' && !Number.isNaN(start)) buffer.firstStart = start
          }
        } else {
          out = flushInterviewer(out)
          out += `[候选人] ${text}\n`
        }
      }
      const formattedText = flushInterviewer(out).trimEnd()
      commit('SET_FORMATTED_DIALOGUES', formattedText)
      resolve(formattedText)
    })
  },
  setInterviewDuration({ commit }, duration) {
    return new Promise((resolve) => {
      commit('SET_INTERVIEW_DURATION', duration)
      resolve()
    })
  },
  setActualInterviewDuration({ commit }, duration) {
    return new Promise((resolve) => {
      commit('SET_ACTUAL_INTERVIEW_DURATION', duration)
      resolve()
    })
  },
  setInterviewLanguage({ commit }, language) { // 新增：设置面试语言
    return new Promise((resolve) => {
      commit('SET_INTERVIEW_LANGUAGE', language)
      resolve()
    })
  },
  setPrompt({ commit }, prompt) {
    commit('SET_PROMPT', prompt)
  },
  setCameraSetting({ commit }, data) {
    const { usingBeautifys, usingFilter, usingBackground } = data
    return new Promise((resolve) => {
      commit('SET_USING_BEAUTIFYS', usingBeautifys)
      commit('SET_USING_FILTER', usingFilter)
      commit('SET_USING_BACKGROUND', usingBackground)
      resolve()
    })
  },
  setInterviewId({ commit }, id) {
    return new Promise((resolve) => {
      commit('SET_INTERVIEW_ID', id)
      resolve()
    })
  },
  startInterview({ commit }) {
    return new Promise((resolve) => {
      commit('SET_INTERVIEW_STARTED', true)
      resolve()
    })
  },
  stopInterview({ commit }) {
    return new Promise((resolve) => {
      commit('SET_INTERVIEW_STARTED', false)
      resolve()
    })
  },
  setJdCv({ commit }, data) {
    const { jd, cv } = data
    return new Promise((resolve) => {
      commit('SET_JD', jd)
      commit('SET_CV', cv)
      resolve()
    })
  },
  dialogueInteract({ commit, dispatch }, data) {
    const { original_text, processed_text_from_llm } = data
    return new Promise((resolve) => {
      if (original_text != null && String(original_text).trim() !== '') {
        commit('SET_DIALOGUES', { userId: '', text: String(original_text).trim() })
      }
      if (processed_text_from_llm != null && String(processed_text_from_llm).trim() !== '') {
        commit('SET_DIALOGUES', { userId: 'RobotMan', text: String(processed_text_from_llm).trim() })
      }
      dispatch('formatDialogues').then(resolve) // 自动触发格式化
    })
  },
  clearDialogues({ commit }) {
    return new Promise((resolve) => {
      commit('CLEAR_DIALOGUES')
      resolve()
    })
  },
  async generatePromptInBackground({ commit, state }, { jdUrl, cvUrl } = {}) {
    const maxRetries = 3
    const jobUrl = jdUrl || state.jd?.url
    const resumeUrl = cvUrl || state.cv?.url

    if (!jobUrl || !resumeUrl) {
      return null
    }

    console.debug('[interviewState] generatePromptInBackground start', {
      jobUrl: jobUrl ? String(jobUrl).slice(0, 60) : '',
      resumeUrl: resumeUrl ? String(resumeUrl).slice(0, 60) : ''
    })

    commit('SET_PROMPT_ERROR', null)
    commit('SET_PROMPT_LOADING', true)

    try {
      const interviewApi = await import('@/api/interview')
      const generateQuestions = interviewApi.generateInterviewQuestions

      let lastError = null

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.debug('[interviewState] generatePromptInBackground attempt', { attempt })
          console.debug('[interviewState] calling /api/interview/questions/generate', {
            jdUrlPresent: !!jobUrl,
            resumeUrlPresent: !!resumeUrl
          })

          const allowedCounts = [10, 20, 30]
          const raw = Number(state.interviewDuration)
          const questionCount = allowedCounts.includes(raw) ? raw : 10

          const response = await generateQuestions({
            jdUrl: jobUrl,
            resumeUrl,
            questionCount
          })


          const data = response && response.data && response.data.data
          if (!data) {
            lastError = new Error('Invalid questions/generate response')
            console.error(`generatePromptInBackground attempt ${attempt} failed:`, lastError)
          } else {
            const isGenerateQuestionsC =
              data.output_type === 'generate_questions_c' ||
              (data.jd_text != null && data.questions != null)

            if (isGenerateQuestionsC) {
              const { buildPromptToCFromGenerateData } = await import('@/utils/buildPromptToCFromGenerate.js')
              const { prompt, questionOutline, candidateName } = buildPromptToCFromGenerateData(data)
              commit('SET_PROMPT', prompt)
              commit('SET_TO_C_INTERVIEW_META', { questionOutline, candidateName })
            } else {
              // 尽量兼容后端返回结构：如果有 prompt 就直接写入；否则至少保证 prompt 为字符串避免后续计算异常
              const prompt =
                data.prompt ?? data.systemPrompt ?? data.llmPrompt ?? ''

              commit('SET_PROMPT', prompt)
              commit('SET_TO_C_INTERVIEW_META', null)
            }

            const interviewId = data.interview_id ?? data.interviewId
            if (interviewId != null && interviewId !== '') {
              commit('SET_INTERVIEW_ID', interviewId)
            } else {
              // 与 token 流程一致：兜底生成 interviewId，避免 waitingForPrompt 永远等待
              commit('SET_INTERVIEW_ID', actions._generateInterviewId())
            }

            return data
          }
        } catch (error) {
          lastError = error
          console.error(`generatePromptInBackground attempt ${attempt} failed:`, error)
        }

        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, attempt * 1000))
        }
      }

      if (lastError) {
        commit('SET_PROMPT_ERROR', lastError)
      }
      return null
    } catch (error) {
      console.error('generatePromptInBackground failed (module or unexpected error):', error)
      commit('SET_PROMPT_ERROR', error)
      return null
    } finally {
      commit('SET_PROMPT_LOADING', false)
    }
  },
  setCurrentInterviewJob({ commit }, job) {
    commit('SET_CURRENT_INTERVIEW_JOB', job)
  },
  clearCurrentInterviewJob({ commit }) {
    commit('CLEAR_CURRENT_INTERVIEW_JOB')
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}